import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import Hls from 'hls.js';
import type { SeekToOptions } from '../hooks/usePlayerState';
import XTAMSpinner from './XTAMSpinner';
import './CameraVideoPlayer.css';



/** Ref expuesto para el conductor de sincronización multi-cámara */

export interface CameraVideoPlayerRef {

  getCurrentTime(): number;
  seekTo(seconds: number, options?: SeekToOptions): void;
  getDuration(): number;
  play(): Promise<void>;
  pause(): void;
  captureFrame(): string | null;

}



interface CameraVideoPlayerProps {

  cameraId: string;
  cameraName: string;
  hlsUrl?: string;
  disableLiveFallback?: boolean;
  placeholderMessage?: string;
  recordingStartTime?: Date | null;
  recordingAnchorWallTime?: Date | null;
  startOffsetSeconds?: number;
  isPlaying?: boolean;
  playbackRate?: number;
  useLowLatency?: boolean; 

  /** Cámaras activas en la grilla (para ajustar prefetch HLS según concurrencia) */
  activeCameraCount?: number;

  onError?: (error: string) => void;
  onPlaybackTimeUpdate?: (seconds: number) => void;
  onStreamStatusChange?: (status: 'loading' | 'playing' | 'error' | 'offline' | 'placeholder') => void;
  onRecordingSeekAnchorApplied?: () => void;
  onHlsBufferReset?: () => void;
  /** Velocidad de carga HLS (bytes/s) estimada; p. ej. para tooltip de ping. */
  onLoadSpeedChange?: (bytesPerSec: number | null) => void;

}



const VIDEO_ENGINE_URL = process.env.REACT_APP_VIDEO_ENGINE_URL || 'http://localhost:9090';



/**

 * Reproductor de vídeo por cámara: HLS (vivo o grabación) y opcionalmente baja latencia por WebSocket/MSE.

 * No es la página “Streams” ni el modo streaming global; es solo el tile de una cámara.

 * Con ref expone getCurrentTime/seekTo/getDuration para sincronización multi-cámara.

 */

const CameraVideoPlayer = forwardRef<CameraVideoPlayerRef | null, CameraVideoPlayerProps>(({

  cameraId,
  cameraName,
  hlsUrl,
  disableLiveFallback = false,
  placeholderMessage = 'Selecciona un rango en el timeline para reproducir',
  recordingStartTime,
  recordingAnchorWallTime,
  startOffsetSeconds,
  isPlaying: isPlayingProp,
  playbackRate = 1,
  useLowLatency = false,
  activeCameraCount = 4,
  onError,
  onPlaybackTimeUpdate,
  onStreamStatusChange,
  onRecordingSeekAnchorApplied,
  onHlsBufferReset,
  onLoadSpeedChange,

}, ref) => {

  const recordingSeekAnchorCbRef = useRef(onRecordingSeekAnchorApplied);
  const startOffsetSecondsRef = useRef(startOffsetSeconds);
  const recordingStartTimeRef = useRef(recordingStartTime);
  const recordingAnchorWallTimeRef = useRef(recordingAnchorWallTime);
  const onErrorRef = useRef(onError);
  const cameraNameRef = useRef(cameraName);
  const onPlaybackTimeUpdateRef = useRef(onPlaybackTimeUpdate);
  const onStreamStatusChangeRef = useRef(onStreamStatusChange);
  const onLoadSpeedChangeRef = useRef(onLoadSpeedChange);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const firstFrameHandlerRef = useRef<(() => void) | null>(null);
  const recordingPreviewFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPlayingRef = useRef<boolean | undefined>(isPlayingProp);
  const prevPlaybackRateForHlsRef = useRef<number | null>(null);
  const recordingAnchorSeekTargetRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  const pendingBuffers = useRef<ArrayBuffer[]>([]);
  const getHlsUrlRef = useRef<(() => string) | null>(null);
  const initWebSocketRef = useRef<(() => Promise<boolean>) | null>(null);
  const initHlsRef = useRef<(() => void) | null>(null);
  const [status, setStatus] = useState<'loading' | 'playing' | 'error' | 'offline' | 'placeholder'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [streamType, setStreamType] = useState<'hls' | 'ws'>('hls');
  const [playbackTime, setPlaybackTime] = useState(0); // segundos desde inicio (para grabación)
  const [playbackDuration, setPlaybackDuration] = useState(0); // duración total si está disponible



  useImperativeHandle(ref, () => ({

    getCurrentTime: () => videoRef.current?.currentTime ?? 0,
    seekTo: (seconds: number, options?: SeekToOptions) => {

      const v = videoRef.current;

      if (!v || !Number.isFinite(seconds)) return;
      const t = Math.max(0, seconds);

      if (options?.skipIfClose && Math.abs(v.currentTime - t) < 0.12) return;
      v.currentTime = t;

    },

    getDuration: () => videoRef.current?.duration ?? 0,
    play: () => videoRef.current?.play() ?? Promise.resolve(),
    pause: () => { videoRef.current?.pause(); },
    captureFrame: (): string | null => {

      const v = videoRef.current;
      if (!v || v.videoWidth === 0 || v.videoHeight === 0) return null;
      try {

        const canvas = document.createElement('canvas');
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(v, 0, 0);
        return canvas.toDataURL('image/jpeg', 0.85);

      } catch {
        return null;
      }

    },

    isBuffering: (): boolean => {

      const v = videoRef.current;
      if (!v) return true;
      return v.readyState < 3;
    },

  }), []);



  isPlayingRef.current = isPlayingProp;
  recordingSeekAnchorCbRef.current = onRecordingSeekAnchorApplied;
  const onHlsBufferResetRef = useRef(onHlsBufferReset);
  onHlsBufferResetRef.current = onHlsBufferReset;
  startOffsetSecondsRef.current = startOffsetSeconds;
  recordingStartTimeRef.current = recordingStartTime;
  recordingAnchorWallTimeRef.current = recordingAnchorWallTime;
  onErrorRef.current = onError;
  cameraNameRef.current = cameraName;
  onPlaybackTimeUpdateRef.current = onPlaybackTimeUpdate;
  onStreamStatusChangeRef.current = onStreamStatusChange;
  onLoadSpeedChangeRef.current = onLoadSpeedChange;



  const recordingStartWallMs =

    recordingStartTime != null && Number.isFinite(recordingStartTime.getTime())
      ? recordingStartTime.getTime()
      : NaN;

  const recordingAnchorWallMs =

    recordingAnchorWallTime != null && Number.isFinite(recordingAnchorWallTime.getTime())
      ? recordingAnchorWallTime.getTime()
      : NaN;

  const startOffsetKey =

    startOffsetSeconds != null && Number.isFinite(startOffsetSeconds)
      ? Math.round(startOffsetSeconds * 1000) / 1000
      : 0;

  // Build HLS URL

  const getHlsUrl = useCallback(() => {

    if (hlsUrl) return hlsUrl;
    return `${VIDEO_ENGINE_URL}/hls/${cameraId}/stream.m3u8`;
  }, [cameraId, hlsUrl]);

  // Build WebSocket URL

  const getWsUrl = useCallback(() => {

    const baseUrl = VIDEO_ENGINE_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    return `${baseUrl}/api/v1/cameras/${cameraId}/ws`;

  }, [cameraId]);



  // Append buffer to MediaSource
  const appendBuffer = useCallback((data: ArrayBuffer) => {
    const sourceBuffer = sourceBufferRef.current;
    if (!sourceBuffer || sourceBuffer.updating) {
      pendingBuffers.current.push(data);
      return;
    }

    try {
      sourceBuffer.appendBuffer(data);
    } catch (e) {
      console.error('[CameraVideoPlayer] Buffer append error:', e);
    }

  }, []);



  // Process pending buffers

  const processPendingBuffers = useCallback(() => {

    const sourceBuffer = sourceBufferRef.current;

    if (!sourceBuffer || sourceBuffer.updating || pendingBuffers.current.length === 0) {
      return;
    }



    const buffer = pendingBuffers.current.shift();
    if (buffer) {

      try {
        sourceBuffer.appendBuffer(buffer);
      } catch (e) {

        console.error('[CameraVideoPlayer] Pending buffer error:', e);

      }

    }

  }, []);



  // Initialize WebSocket streaming (low-latency)

  const initWebSocket = useCallback(async () => {

    const video = videoRef.current;
    if (!video) return false;

    if (!('MediaSource' in window)) {
      console.log('[CameraVideoPlayer] MSE not supported, falling back to HLS');
      return false;

    }



    setStreamType('ws');

    setStatus('loading');



    // Create MediaSource

    const mediaSource = new MediaSource();

    mediaSourceRef.current = mediaSource;

    video.src = URL.createObjectURL(mediaSource);



    await new Promise<void>((resolve, reject) => {

      mediaSource.addEventListener('sourceopen', () => {

        try {

          const sourceBuffer = mediaSource.addSourceBuffer('video/mp4; codecs="avc1.42E01E, mp4a.40.2"');

          sourceBufferRef.current = sourceBuffer;



          sourceBuffer.addEventListener('updateend', () => {

            processPendingBuffers();

            if (video.paused && video.readyState >= 2) {

              video.play().catch(() => { });

            }

          });



          resolve();

        } catch (e) {

          reject(e);

        }

      });

    });



    // Connect WebSocket

    const ws = new WebSocket(getWsUrl());

    ws.binaryType = 'arraybuffer';

    wsRef.current = ws;



    ws.onopen = () => {

      console.log('[CameraVideoPlayer] WebSocket connected');

      setStatus('playing');

    };



    ws.onmessage = (event) => {

      if (event.data instanceof ArrayBuffer) {

        appendBuffer(event.data);

      }

    };



    ws.onerror = () => {

      console.error('[CameraVideoPlayer] WebSocket error, falling back to HLS');

      return false;

    };



    ws.onclose = () => {

      console.log('[CameraVideoPlayer] WebSocket closed');

    };



    return true;

  }, [getWsUrl, appendBuffer, processPendingBuffers]);



  /** If the server response indicates "no stream available", return user-friendly message instead of showing raw JSON */

  const getStreamErrorUserMessage = (responseBody: string | undefined): string | null => {

    if (!responseBody || typeof responseBody !== 'string') return null;

    const trimmed = responseBody.trim();

    if (/no stream is available/i.test(trimmed)) return 'SIN SEÑAL';

    try {

      const parsed = JSON.parse(trimmed) as { status?: string; error?: string };

      if (parsed?.status === 'error' && typeof parsed?.error === 'string' && /no stream/i.test(parsed.error)) {

        return 'SIN SEÑAL';

      }

    } catch {

      // not JSON, already checked with regex above

    }

    return null;

  };



  /** Normalize any error message for display: never show raw JSON; show "SIN SEÑAL" for no-stream errors */

  const displayErrorMessage = (msg: string | null | undefined, fallback: string): string => {

    const raw = msg?.trim() ?? '';

    if (!raw) return fallback;

    if (/no stream is available/i.test(raw)) return 'SIN SEÑAL';

    if (/^\s*\{\s*"status"\s*:\s*"error"/.test(raw) && /"error"\s*:.*no stream/i.test(raw)) return 'SIN SEÑAL';

    try {

      const parsed = JSON.parse(raw) as { status?: string; error?: string };

      if (parsed?.status === 'error' && typeof parsed?.error === 'string' && /no stream/i.test(parsed.error)) {

        return 'SIN SEÑAL';

      }

    } catch {

      // not JSON

    }

    return raw;

  };



  // Build descriptive error message for HLS failures (helps diagnose playback vs live issues)

  const buildHlsErrorMessage = (

    errorType: number,

    details: string,

    isPlaybackUrl: boolean,

    httpCode?: number,

  ): string => {

    const isNetworkError = errorType === 2; // Hls.ErrorTypes.NETWORK_ERROR

    if (isPlaybackUrl) {

      if (isNetworkError) {

        if (details === 'manifestLoadError' || details === 'manifestLoadTimeOut') {

          return httpCode === 404

            ? 'Grabación no encontrada (404). La solicitud puede haber expirado.'

            : `No se pudo cargar la grabación. ¿La URL es accesible? Revisa VIDEO_ENGINE_PUBLIC_URL si accedes desde otra máquina (HTTP ${httpCode || 'error'}).`;

        }

        if (details === 'fragLoadError' || details === 'fragLoadTimeOut') {

          return httpCode === 404

            ? 'Fragmento de vídeo no encontrado (404). Compruebe que existan los .ts en el almacenamiento, que la sesión sea válida y use «Reintentar» o vuelva a preparar la grabación.'

            : 'Error al cargar segmentos de la grabación. Puede ser CORS o red.';

        }

        if (details === 'levelLoadError' || details === 'levelLoadTimeOut') {

          return httpCode === 404

            ? 'Lista HLS no encontrada (404). Si el motor de vídeo se reinició, la solicitud anterior deja de existir.'

            : 'No se pudo cargar la lista HLS de la grabación.';

        }

        return 'Error de red al cargar la grabación. Comprueba que VIDEO_ENGINE_PUBLIC_URL sea accesible desde tu navegador.';

      }

      return 'Error de reproducción de la grabación. Revisa la consola del navegador para más detalles.';

    }

    if (isNetworkError) {

      return 'Cámara offline o sin stream';

    }

    return 'Error de reproducción';

  };



  // Espera al primer frame pintado por el navegador antes de pasar a `playing` y ocultar el overlay.

  // Sin esto se ve un instante en negro entre el `seeked`/`canplay` (datos listos) y la pintura real

  // del frame en MSE tras el seek al ancla. Tiene salvavidas por si el callback no se dispara.

  const revealOnNextPaint = useCallback(() => {

    const v = videoRef.current as

      | (HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number })

      | null;

    if (!v) {

      setStatus('playing');

      return;

    }

    const rvfc = v.requestVideoFrameCallback;

    if (typeof rvfc !== 'function') {

      // Firefox no expone rVFC: pequeño retardo para que la pintura ocurra antes de quitar el overlay.

      window.setTimeout(() => setStatus('playing'), 80);

      return;

    }

    let revealed = false;

    const reveal = () => {

      if (revealed) return;

      revealed = true;

      setStatus('playing');

    };

    rvfc.call(v, reveal);

    window.setTimeout(reveal, 800);

  }, []);



  // Initialize HLS streaming.

  // Recording (hlsUrl) uses progressive playlists: we do NOT wait for full M3U8; we start as soon as

  // the manifest exists and the first segment is available. Retry manifest load on 404 (playlist not ready yet).

  const initHls = useCallback(() => {

    const video = videoRef.current;

    if (!video) return;



    setStreamType('hls');

    setStatus('loading');



    const streamUrl = getHlsUrl();

    const isRecordingUrl = Boolean(hlsUrl);

    const loadStartTime = Date.now();

    console.log('[CameraVideoPlayer] Loading HLS:', streamUrl, isRecordingUrl ? '(recording – progressive)' : '');



    // Solo grabaciones (hlsUrl): opciones extra; streams en vivo no reciben estas claves (defaults de hls.js).

    const so = startOffsetSecondsRef.current;

    /**
     * Posición (s) en el eje `currentTime` del <video> que corresponde al *ancla* (puntero).
     * `recordingStartTime` (playback.startTime) NO es el t=0 del playlist: es el ancla, y vive en
     * `currentTime = startOffsetSeconds` (ver wallClockMsFromPlaybackVideo en usePlayerState). Por eso
     * el destino es `startOffsetSeconds + (ancla − startTime)`; quitar `so` dejaba este seek `so`
     * segundos por detrás de play()/stop()/seekBySecondsLocal → el vídeo saltaba al pausar y
     * rebotaba entre dos objetivos (bucle).
     */
    const soBase = so != null && Number.isFinite(so) ? Math.max(0, so) : 0;
    let recordingStartPos = soBase;

    if (isRecordingUrl) {

      const rs = recordingStartTimeRef.current;

      const ra = recordingAnchorWallTimeRef.current;

      if (

        ra != null &&

        rs != null &&

        Number.isFinite(rs.getTime()) &&

        Number.isFinite(ra.getTime()) &&

        ra.getTime() >= rs.getTime()

      ) {

        recordingStartPos = soBase + (ra.getTime() - rs.getTime()) / 1000;

      }

    }

    recordingAnchorSeekTargetRef.current = isRecordingUrl ? recordingStartPos : 0;

    const recordingOnlyHlsConfig: Partial<Hls['config']> | undefined = isRecordingUrl

      ? {

        // EVENT en crecimiento: edge saltaba a la "live" del M3U8; peleaba con el seek al ancla (bucle buffer).

        liveSyncMode: 'buffered',

        liveDurationInfinity: false,

      }

      : undefined;



    if (Hls.isSupported()) {

      const hls = new Hls({

        enableWorker: true,

        lowLatencyMode: false,

        // Con ≤2 cámaras el pool de 6 conexiones del browser alcanza; con más se deshabilita
        // para evitar que los prefetch encolen el primer .ts de otra cámara.
        startFragPrefetch: isRecordingUrl ? activeCameraCount <= 2 : false,

        liveSyncMode: 'edge',

        // Grabaciones: empezar a descargar/posicionar en el ancla (puntero), no en t=0

        // (el playlist arranca 5 min antes con el preroll). Sin esto el primer frame es

        // del preroll y el seek tarda en aplicar hasta que el buffer alcanza el ancla.

        startPosition: isRecordingUrl ? recordingStartPos : -1,

        ...(recordingOnlyHlsConfig ?? {}),

        backBufferLength: isRecordingUrl ? 30 : 30,

        // Con 4 cámaras y HTTP/1.1 (6 conex por origen) un buffer ancho mantenía .ts
        // encolados detrás de fetches que no eran los inmediatos → underrun en otras
        // cámaras. Bajamos a 30/90 (≈3-9 .ts de 10s) para que cada cámara apriete poco
        // y comparta el pool de conexiones de forma equitativa.
        maxBufferLength: isRecordingUrl ? 30 : 15,

        maxMaxBufferLength: isRecordingUrl ? 90 : 60,

        maxBufferHole: isRecordingUrl ? 0.8 : 0.1,

        maxFragLookUpTolerance: isRecordingUrl ? 0.75 : 0.25,

        nudgeOffset: isRecordingUrl ? 0.3 : 0.1,

        nudgeMaxRetry: isRecordingUrl ? 8 : 3,

        manifestLoadingTimeOut: isRecordingUrl ? 3000 : 5000,

        // Grabaciones: 3 reintentos rápidos antes de ceder al manejador externo (recording404Retryable).
        // 8 internos × 150 ms = 1,2 s/ciclo × 20 ciclos externos = 24 s y 160 peticiones al m3u8.
        // Con 3 × 150 ms = 0,45 s/ciclo × 20 ciclos = 9 s y 80 peticiones — más razonable.
        manifestLoadingMaxRetry: isRecordingUrl ? 3 : 3,

        manifestLoadingRetryDelay: isRecordingUrl ? 150 : 1000,

        levelLoadingTimeOut: 5000,

        // Más reintentos de la lista de niveles: el video-engine refresca la m3u8 mientras
        // genera segmentos nuevos; un 404 transitorio no debe tirar el stream.
        levelLoadingMaxRetry: isRecordingUrl ? 6 : 4,

        levelLoadingRetryDelay: isRecordingUrl ? 150 : 1000,

        fragLoadingTimeOut: isRecordingUrl ? 30000 : 15000,

        // Reintentos generosos en grabaciones: el TS server (NFS) materializa segmentos lazy y
        // con varias cámaras compitiendo por las 6 conexiones del navegador, un .ts puede fallar
        // por timeout/404 transitorio. 6 reintentos con backoff cubren el caso sin colgar el stream.
        fragLoadingMaxRetry: isRecordingUrl ? 6 : 2,

        fragLoadingRetryDelay: isRecordingUrl ? 300 : 1000,

        startLevel: 0,

        // Recordings: local video-engine has high bandwidth; skip ABR ramp-up.

        testBandwidth: !isRecordingUrl,

        abrEwmaDefaultEstimate: isRecordingUrl ? 8_000_000 : 500_000,

        // Avoid edge-seek when buffer temporarily runs dry during segment loading.

        maxStarvationDelay: isRecordingUrl ? 4 : 4,

        // Stretch short trailing audio tracks to avoid gaps between segments.

        stretchShortVideoTrack: true,

      });



      hlsRef.current = hls;

      // Map HLS events to timing metrics and existing visual progress (loading → playing).

      hls.on(Hls.Events.MANIFEST_PARSED, () => {

        const manifestParsedMs = Date.now() - loadStartTime;

        console.log(`[TTFF] ${cameraNameRef.current} manifest_parsed=${manifestParsedMs}ms`);

        if (!isRecordingUrl) setStatus('playing');

        if (isPlayingRef.current !== false) {

          video.play().catch(() => { });

        }

      });

      let firstFragLogged = false;

      hls.on(Hls.Events.FRAG_LOADED, (_e, data) => {

        // Estimación de velocidad de carga (bytes/s) para tooltips de red.
        const stats = (data as { stats?: { total?: number; loading?: { start?: number; end?: number } } }).stats;
        const total = stats?.total ?? 0;
        const loadMs =
          stats?.loading?.start != null && stats?.loading?.end != null
            ? stats.loading.end - stats.loading.start
            : 0;
        let bytesPerSec: number | null = null;
        if (total > 0 && loadMs > 0) {
          bytesPerSec = Math.round((total * 1000) / loadMs);
        } else if (typeof hls.bandwidthEstimate === 'number' && hls.bandwidthEstimate > 0) {
          // hls.js bandwidthEstimate está en bits/s
          bytesPerSec = Math.round(hls.bandwidthEstimate / 8);
        }
        if (bytesPerSec != null && bytesPerSec > 0) {
          onLoadSpeedChangeRef.current?.(bytesPerSec);
        }

        if (firstFragLogged) return;

        firstFragLogged = true;

        const fragLoadedMs = Date.now() - loadStartTime;

        // FRAG_LOADED fires after hls.js finishes receiving the first segment (or its first chunk).

        // With chunked streaming this fires much sooner than the 60 s segment duration.

        console.log(`[TTFF] ${cameraNameRef.current} first_frag_loaded=${fragLoadedMs}ms sn=${(data as any).frag?.sn ?? '?'}`);

      });

      // En grabaciones el manifest se refresca cada pocos s; loguear cada LEVEL_LOADED satura la consola

      // y no aporta (el problema de buffering era live sync al borde, no el refresco en sí).

      if (!isRecordingUrl) {

        hls.on(Hls.Events.LEVEL_LOADED, (_e, data) => {

          const levelLoadedMs = Date.now() - loadStartTime;

          console.log(

            `[TTFF] ${cameraNameRef.current} level_loaded=${levelLoadedMs}ms level=${data.level} frags=${data.details?.fragments?.length ?? '?'}`,

          );

        });

      }



      // Primer frame decodificado: en grabaciones con seek al ancla, el primer loadeddata suele ser ~t=0

      // antes de saltar al instante elegido; si quitamos el overlay aquí, queda negro hasta el segmento.

      // Solo pasamos a "playing" cuando currentTime está cerca del objetivo (o no hay salto relevante).

      const ANCHOR_FRAME_TOLERANCE_SEC = 2.5;

      const onFirstFrame = () => {

        const firstFrameMs = Date.now() - loadStartTime;

        const anchorTarget = recordingAnchorSeekTargetRef.current;

        if (

          isRecordingUrl &&

          anchorTarget > 0.05 &&

          Math.abs(video.currentTime - anchorTarget) > ANCHOR_FRAME_TOLERANCE_SEC

        ) {

          return;

        }

        console.log(`[TTFF] ${cameraNameRef.current} first_frame_rendered=${firstFrameMs}ms  ← KEY METRIC`);

        if (isRecordingUrl) {

          // Esperar al primer frame realmente pintado por el navegador; evita el instante en negro

          // entre `loadeddata`/`canplay` (data lista) y la decodificación efectiva del frame del ancla.

          revealOnNextPaint();

        }

        video.removeEventListener('loadeddata', onFirstFrame);

        video.removeEventListener('canplay', onFirstFrame);

        firstFrameHandlerRef.current = null;

      };

      firstFrameHandlerRef.current = onFirstFrame;

      video.addEventListener('loadeddata', onFirstFrame);

      video.addEventListener('canplay', onFirstFrame);



      // hls.js does NOT retry on HTTP 4xx errors by default (only on timeouts/network errors).

      // For recording playlists the video-engine can take a while to create the manifest (NFS, FFmpeg).

      // We retry for a long window so the player waits instead of failing early.

      let manifestNotReadyRetries = 0;

      const MAX_MANIFEST_NOT_READY_RETRIES = 20; // 20 × 250 ms = 5 s

      const MANIFEST_RETRY_DELAY_MS = 250;

      // Tras agotar fragLoadingMaxRetry, hls.js declara error fatal de red. Para grabaciones,
      // reintentamos hls.startLoad() unas veces antes de marcar la cámara offline: cubre el caso
      // en que un .ts tarda en materializarse en NFS más allá del presupuesto de hls.js.
      let fatalNetworkRecoveries = 0;

      const MAX_FATAL_NETWORK_RECOVERIES = isRecordingUrl ? 4 : 0;

      const FATAL_NETWORK_RECOVERY_BASE_DELAY_MS = 2000;

      let mediaErrorRecoveries = 0;

      const MAX_MEDIA_ERROR_RECOVERIES = 3;



      hls.on(Hls.Events.ERROR, (_, data) => {

        const details = (data as any).details || '';

        const resp = (data as any).response;

        const httpCode = resp?.code ?? 0;

        const failUrl =

          typeof (data as any).context?.url === 'string'

            ? (data as any).context.url

            : typeof (data as any).url === 'string'

              ? (data as any).url

              : '';



        // Retry recording: manifest/level not ready yet, or stream.m3u8 404 while playlist is still

        // materializing on NFS; hls.js may surface that as fragLoadError on level refresh.

        // Solo errores del MANIFIESTO disparan loadSource() para reintentar la preparación.
        // levelLoadError/levelLoadTimeOut son errores de la media playlist que HLS.js ya reintenta
        // internamente (levelLoadingMaxRetry). Incluirlos aquí causa hls.loadSource() repetido
        // (destruye todo el pipeline y recarga el m3u8 completo en bucle).
        const recording404Retryable =

          isRecordingUrl &&

          (httpCode === 404 || httpCode === 0) &&

          (details === 'manifestLoadError' ||

            details === 'manifestLoadTimeOut' ||

            // Manifest refresh sometimes reported as FRAG_* ; .ts 404 must not loop here.

            ((details === 'fragLoadError' || details === 'fragLoadTimeOut') &&

              typeof failUrl === 'string' &&

              failUrl.includes('stream.m3u8')));



        if (recording404Retryable && manifestNotReadyRetries < MAX_MANIFEST_NOT_READY_RETRIES) {

          manifestNotReadyRetries++;

          console.log(

            `[TTFF] ${cameraNameRef.current} recording_hls_retry=${manifestNotReadyRetries}/${MAX_MANIFEST_NOT_READY_RETRIES} details=${details} url=${failUrl || streamUrl}`,

          );

          setTimeout(() => {

            if (hlsRef.current === hls) {

              hls.loadSource(streamUrl);

            }

          }, MANIFEST_RETRY_DELAY_MS);

          return;

        }



        if (data.fatal) {

          let respBody: string = '';

          if (typeof resp?.text === 'string') respBody = resp.text;

          else if (typeof (resp as any)?.data === 'string') respBody = (resp as any).data;

          else if (typeof (resp as any)?.responseText === 'string') respBody = (resp as any).responseText;

          else if (resp && typeof (resp as any).data === 'object' && (resp as any).data !== null) {

            const obj = (resp as any).data;

            if (obj?.status === 'error' && /no stream/i.test(String(obj?.error ?? ''))) respBody = JSON.stringify(obj);

          }

          const respText = respBody.slice(0, 300);

          console.error('[CameraVideoPlayer] HLS fatal error:', {

            type: data.type,

            details,

            httpCode,

            streamUrl,

            failUrl: failUrl || undefined,

            isPlaybackUrl: isRecordingUrl,

            responseText: respText || undefined,

          });

          const noStreamMsg = getStreamErrorUserMessage(respBody);

          const userMsg = noStreamMsg ?? buildHlsErrorMessage(Number(data.type), details, isRecordingUrl, httpCode);

          switch (data.type) {

            case Hls.ErrorTypes.NETWORK_ERROR:

              if (fatalNetworkRecoveries < MAX_FATAL_NETWORK_RECOVERIES) {

                fatalNetworkRecoveries++;

                const delay = FATAL_NETWORK_RECOVERY_BASE_DELAY_MS * Math.pow(2, fatalNetworkRecoveries - 1);

                console.log(

                  `[TTFF] ${cameraNameRef.current} fatal_network_recover=${fatalNetworkRecoveries}/${MAX_FATAL_NETWORK_RECOVERIES} delay=${delay}ms details=${details} url=${failUrl || streamUrl}`,

                );

                setStatus('loading');

                setTimeout(() => {

                  if (hlsRef.current === hls) {

                    try { hls.startLoad(); } catch { /* ignore */ }

                  }

                }, delay);

                break;

              }

              setStatus('offline');

              setErrorMessage(userMsg);

              onErrorRef.current?.(userMsg);

              break;

            case Hls.ErrorTypes.MEDIA_ERROR:

              if (mediaErrorRecoveries < MAX_MEDIA_ERROR_RECOVERIES) {

                mediaErrorRecoveries++;

                console.log(

                  `[TTFF] ${cameraNameRef.current} media_error_recover=${mediaErrorRecoveries}/${MAX_MEDIA_ERROR_RECOVERIES} details=${details}`,

                );

                hls.recoverMediaError();

              } else {

                setStatus('error');

                setErrorMessage(userMsg);

                onErrorRef.current?.(userMsg);

              }

              break;

            default:

              setStatus('error');

              setErrorMessage(userMsg);

              onErrorRef.current?.(userMsg);

              break;

          }

        }

      });



      hls.loadSource(streamUrl);

      hls.attachMedia(video);



      // Posicionar al ancla lo hace `startPosition` (config Hls) + el `seekIfReady` del useEffect

      // [hlsUrl, startOffsetKey, ...]. Un handler extra en FRAG_BUFFERED re-seekaba en cada fragmento

      // si currentTime se desviaba >0,5 s del ancla durante reproducción → bucle de descarga del mismo .ts.

    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {

      // Safari native HLS

      const ANCHOR_FRAME_TOLERANCE_SEC = 2.5;

      video.src = streamUrl;

      video.addEventListener('loadedmetadata', () => {

        const target = recordingAnchorSeekTargetRef.current;

        if (

          isRecordingUrl &&

          target > 0.05 &&

          Math.abs(video.currentTime - target) > ANCHOR_FRAME_TOLERANCE_SEC

        ) {

          return;

        }

        setStatus('playing');

        if (isPlayingRef.current !== false) {

          video.play().catch(() => setStatus('playing'));

        }

      });

      video.addEventListener('error', () => {

        setStatus('offline');

        setErrorMessage('Cámara offline');

        onErrorRef.current?.('Cámara offline');

      });

    } else {

      setStatus('error');

      setErrorMessage('HLS no soportado');

      onErrorRef.current?.('HLS no soportado');

    }

    // Solo hlsUrl/cámara: si metemos startOffset/onError/cameraName aquí, cada render puede recrear initHls

    // y el useEffect principal hace cleanup()+init → recarga continua del manifest/.ts.

    // `revealOnNextPaint` está memoizado con deps vacías → identidad estable, no provoca recreate.

  }, [getHlsUrl, hlsUrl, revealOnNextPaint]);



  getHlsUrlRef.current = getHlsUrl;

  initWebSocketRef.current = initWebSocket;

  initHlsRef.current = initHls;



  // Cleanup (per-camera independent; no shared state with other slots).

  const cleanup = useCallback(() => {

    const v = videoRef.current;

    const firstFrameHandler = firstFrameHandlerRef.current;

    if (v && firstFrameHandler) {

      v.removeEventListener('loadeddata', firstFrameHandler);

      v.removeEventListener('canplay', firstFrameHandler);

      firstFrameHandlerRef.current = null;

    }

    if (hlsRef.current) {

      // Release HLS in explicit order when a slot is removed/replaced:

      // stop network loading -> detach media element -> destroy instance.

      // This prevents stale playlist polling and frees memory aggressively.

      try { hlsRef.current.stopLoad(); } catch { }

      try { hlsRef.current.detachMedia(); } catch { }

      hlsRef.current.destroy();

      hlsRef.current = null;

    }

    onLoadSpeedChangeRef.current?.(null);

    if (wsRef.current) {

      wsRef.current.close();

      wsRef.current = null;

    }

    if (mediaSourceRef.current?.readyState === 'open') {

      try {

        mediaSourceRef.current.endOfStream();

      } catch (e) { }

    }

    mediaSourceRef.current = null;

    sourceBufferRef.current = null;

    pendingBuffers.current = [];



    // Fully reset the video element so the freed slot has no stale media source.

    if (v) {

      v.pause();

      const src = v.src;

      if (src && src.startsWith('blob:')) {

        try {

          URL.revokeObjectURL(src);

        } catch {

          /* ignore */

        }

      }

      v.removeAttribute('src');

      v.load();

    }

  }, []);



  // Initialize stream

  useEffect(() => {

    // Página de grabaciones: no intentar stream en vivo si no hay URL de playback

    if (disableLiveFallback && !hlsUrl) {

      cleanup();

      setStatus('placeholder');

      setErrorMessage('');

      return;

    }



    let cancelled = false;



    const init = async () => {

      cleanup();

      setStatus('loading');

      setErrorMessage('');

      // [BENCH] Marca el inicio absoluto de la inicialización de la cámara.
      // Se contrasta con [TTFF] manifest_parsed/first_frame_rendered para ver
      // cuánto se va en preflight vs HLS.js vs primer frame pintado.
      const initStart = performance.now();
      const isRec = Boolean(hlsUrl);
      console.log(`[BENCH] ${cameraNameRef.current} init_start recording=${isRec}`);



      // Try WebSocket first if low latency is requested

      if (useLowLatency) {

        const wsSuccess = await initWebSocketRef.current?.();

        if (wsSuccess) return;

      }



      if (cancelled) return;

      // Sin preflight GET stream.m3u8: hls.js carga el manifest con su propio retry/backoff
      // y los 404/503/timeout caen en el handler ERROR (recording404Retryable) que reintenta
      // loadSource hasta MAX_MANIFEST_NOT_READY_RETRIES. Evita una petición extra por cámara
      // al cargar (4 cámaras = 4 POST, no 8).
      console.log(
        `[BENCH] ${cameraNameRef.current} init_to_initHls_ms=${Math.round(performance.now() - initStart)}`,
      );
      initHlsRef.current?.();

    };



    void init();

    return () => {

      cancelled = true;

      cleanup();

    };

  }, [cameraId, hlsUrl, disableLiveFallback, useLowLatency, cleanup]);



  // Seek inicial al offset del API cuando cambia la URL / ventana HLS (no al pulsar play).

  // - No incluir isPlaying en dependencias: play() en usePlayerState ya hace seekTo(posición correcta

  //   con selectedTime tras pausa); si al pasar a PLAY reaplicáramos solo startOffsetSeconds,

  //   el vídeo saltaría al instante del prepare inicial y vaciaría el buffer.

  // - No escuchar 'playing': tras un stall hls.js reanuda y dispara 'playing' otra vez; seekIfReady

  //   compararía currentTime avanzado con startOffsetSeconds y forzaría un salto atrás (bucle buffer).

  // - initialSeekDone: una vez que el seek inicial se aplica, dejar de re-seekar; si no, canplay

  //   dispara seekIfReady tras cada seek del sync y arrastra el video de vuelta a startOffsetSeconds

  //   creando un bucle seek↔sync infinito con buffering permanente.

  useEffect(() => {

    const video = videoRef.current;

    if (!video || !hlsUrl) return;



    if (recordingPreviewFallbackTimerRef.current) {

      clearTimeout(recordingPreviewFallbackTimerRef.current);

      recordingPreviewFallbackTimerRef.current = null;

    }



    const baseOff = startOffsetSeconds ?? 0;

    let targetSec = baseOff;

    if (

      Number.isFinite(recordingStartWallMs) &&

      Number.isFinite(recordingAnchorWallMs) &&

      recordingAnchorWallMs >= recordingStartWallMs

    ) {

      // `recordingStartTime` (playback.startTime) es el ANCLA en `currentTime = startOffsetSeconds`,
      // no el t=0 del playlist (ver wallClockMsFromPlaybackVideo). Para mapear una hora de pared al
      // `currentTime` del <video> hay que sumar `baseOff`; así este seek coincide con el de play()/
      // stop()/seekBySecondsLocal y el vídeo no salta al pausar ni rebota entre dos objetivos (bucle).
      targetSec = baseOff + (recordingAnchorWallMs - recordingStartWallMs) / 1000;

    }

    if (!Number.isFinite(targetSec) || targetSec < 0) targetSec = 0;



    const gateFirstFrameToAnchor =

      Number.isFinite(recordingStartWallMs) && Number.isFinite(recordingAnchorWallMs);



    const clearPreviewTimer = () => {

      if (recordingPreviewFallbackTimerRef.current) {

        clearTimeout(recordingPreviewFallbackTimerRef.current);

        recordingPreviewFallbackTimerRef.current = null;

      }

    };



    const revealPreviewAfterAnchorSeek = () => {

      if (!gateFirstFrameToAnchor) return;

      clearPreviewTimer();

      // Esperar al primer frame realmente pintado tras el seek; evita el momento en negro entre

      // el evento `seeked` y la decodificación/pintura del frame del ancla en MSE.

      revealOnNextPaint();

    };



    if (gateFirstFrameToAnchor) {

      recordingPreviewFallbackTimerRef.current = setTimeout(() => {

        recordingPreviewFallbackTimerRef.current = null;

        setStatus('playing');

      }, 8_000);

    }



    let anchorApplied = false;

    let initialSeekDone = false;



    const tryNotifyAnchor = (target: number) => {

      const cb = recordingSeekAnchorCbRef.current;

      if (!cb || anchorApplied) return;

      if (video.readyState >= 2 && Math.abs(video.currentTime - target) <= 0.75) {

        anchorApplied = true;

        cb();

      }

    };



    const markInitialSeekAndReveal = (target: number) => {

      if (initialSeekDone) return;

      initialSeekDone = true;

      tryNotifyAnchor(target);

      revealPreviewAfterAnchorSeek();

    };



    const seekIfReady = () => {

      if (initialSeekDone) return;

      if (video.readyState >= 2) {

        let target = targetSec;

        if (Number.isFinite(video.duration) && video.duration > 0 && target >= video.duration) {

          target = Math.max(0, video.duration - 1);

        }

        if (video.seekable.length > 0) {

          const bufEnd = video.seekable.end(video.seekable.length - 1);

          if (bufEnd + 1e-6 < target - 0.25) return;

        } else if (target > 0.05) {

          return;

        }

        if (Math.abs(video.currentTime - target) > 0.5) {

          video.currentTime = target;

          if (isPlayingRef.current) video.play().catch(() => { });

        } else {

          markInitialSeekAndReveal(target);

        }

      }

    };



    const onSeeked = () => {

      if (isPlayingRef.current) video.play().catch(() => { });

      let target = targetSec;

      if (Number.isFinite(video.duration) && video.duration > 0 && target >= video.duration) {

        target = Math.max(0, video.duration - 1);

      }

      // Alineación con GOP/keyframe: más holgura que 0,75 s solo en el 1.º fijado al ancla

      if (!initialSeekDone && Math.abs(video.currentTime - target) <= 1.5) {

        markInitialSeekAndReveal(target);

      } else {

        tryNotifyAnchor(target);

      }

    };



    video.addEventListener('loadedmetadata', seekIfReady);

    video.addEventListener('canplay', seekIfReady);

    video.addEventListener('loadeddata', seekIfReady);

    video.addEventListener('seeked', onSeeked);

    seekIfReady();

    const retryDelaysMs = [100, 300, 600, 1200, 2500, 4000];

    const retryIds = retryDelaysMs.map((ms) => setTimeout(seekIfReady, ms));

    return () => {

      video.removeEventListener('loadedmetadata', seekIfReady);

      video.removeEventListener('canplay', seekIfReady);

      video.removeEventListener('loadeddata', seekIfReady);

      video.removeEventListener('seeked', onSeeked);

      retryIds.forEach(clearTimeout);

      clearPreviewTimer();

    };

  }, [hlsUrl, startOffsetKey, recordingStartWallMs, recordingAnchorWallMs, revealOnNextPaint]);



  // Sincronizar play/pause cuando isPlayingProp cambia (control desde RecordingsPage)

  useEffect(() => {

    const video = videoRef.current;

    if (!video || isPlayingProp == null) return;

    if (isPlayingProp) {

      video.play().catch(() => { });

    } else {

      video.pause();

    }

  }, [isPlayingProp]);



  // Aplicar velocidad de reproducción seleccionada (0.5x, 1x, 2x, 4x, 8x)

  useEffect(() => {

    const video = videoRef.current;

    if (!video) return;

    const safeRate = Number.isFinite(playbackRate) && playbackRate > 0 ? playbackRate : 1;

    video.playbackRate = safeRate;

    video.defaultPlaybackRate = safeRate;

  }, [playbackRate]);



  // Grabaciones HLS: a >1× más buffer y timeout; al bajar la velocidad hay que reiniciar la carga o quedan .ts pending y buffering infinito.

  useEffect(() => {

    if (!hlsUrl) {

      prevPlaybackRateForHlsRef.current = null;

      return;

    }



    const hls = hlsRef.current;

    const video = videoRef.current;

    const next = Number.isFinite(playbackRate) && playbackRate > 0 ? playbackRate : 1;

    const prev = prevPlaybackRateForHlsRef.current;

    prevPlaybackRateForHlsRef.current = next;



    if (!hls?.config) return;



    const c = hls.config as typeof hls.config & {

      fragLoadingTimeOut?: number;

      fragLoadingMaxRetry?: number;

    };

    // Solo timeouts: no tocar maxBufferLength aquí — en grabaciones initHls ya fija 30/90 y

    // al subir velocidad pisarlo con ~12–36 s vacía el buffer y el TS “salta” o vuelve al inicio del ventana.

    if (next > 1) {

      c.fragLoadingTimeOut = Math.min(180000, 95000 + next * 20000);

      c.fragLoadingMaxRetry = 6;

    } else {

      c.fragLoadingTimeOut = 90000;

      c.fragLoadingMaxRetry = 4;

    }



    const decreased =

      prev != null && prev > next + 0.001;



    if (!decreased || !video) return undefined;



    try {

      hls.stopLoad();

    } catch {

      /* ignore */

    }



    const resumeId = window.setTimeout(() => {

      if (hlsRef.current !== hls) return;

      try {

        hls.startLoad();

      } catch {

        /* ignore */

      }

      onHlsBufferResetRef.current?.();

      if (!video.paused && isPlayingRef.current !== false) {

        video.play().catch(() => { });

      }

    }, 80);



    return () => window.clearTimeout(resumeId);

  }, [playbackRate, hlsUrl]);



  // Tiempo de avance (para grabación): timeupdate + intervalo de respaldo para que siempre se vea

  useEffect(() => {

    const video = videoRef.current;

    if (!video || !hlsUrl) return;



    const syncTime = () => {

      const t = video.currentTime;

      const d = video.duration;

      setPlaybackTime(t);

      onPlaybackTimeUpdateRef.current?.(t);

      if (Number.isFinite(d) && d > 0) setPlaybackDuration(d);

    };



    const onTimeUpdate = () => syncTime();

    const onDurationChange = () => {

      const d = video.duration;

      if (Number.isFinite(d) && d > 0) setPlaybackDuration(d);

    };



    video.addEventListener('timeupdate', onTimeUpdate);

    video.addEventListener('durationchange', onDurationChange);

    syncTime();



    const interval = setInterval(() => {

      if (videoRef.current && !videoRef.current.paused) syncTime();

    }, 300);



    return () => {

      video.removeEventListener('timeupdate', onTimeUpdate);

      video.removeEventListener('durationchange', onDurationChange);

      clearInterval(interval);

    };

  }, [hlsUrl, status]);



  useEffect(() => {

    const video = videoRef.current;

    if (!video || !hlsUrl) return;



    const ANCHOR_FRAME_TOLERANCE_SEC = 2.5;

    const onPlaying = () => {

      setStatus((prev) => {

        if (prev !== 'loading') return prev;

        const target = recordingAnchorSeekTargetRef.current;

        if (

          target > 0.05 &&

          Math.abs(video.currentTime - target) > ANCHOR_FRAME_TOLERANCE_SEC

        ) {

          return prev;

        }

        return 'playing';

      });

    };



    video.addEventListener('playing', onPlaying);

    return () => {

      video.removeEventListener('playing', onPlaying);

    };

  }, [hlsUrl]);



  useEffect(() => {

    const video = videoRef.current;

    if (!video || hlsUrl) return;



    let waitingTimer: ReturnType<typeof setTimeout> | null = null;



    const onWaiting = () => {

      if (waitingTimer) return;

      waitingTimer = setTimeout(() => {

        waitingTimer = null;

        if (video.readyState < 3) {

          setStatus((prev) => (prev === 'playing' ? 'loading' : prev));

        }

      }, 800);

    };

    const onPlaying = () => {

      if (waitingTimer) {

        clearTimeout(waitingTimer);

        waitingTimer = null;

      }

      setStatus((prev) => (prev === 'loading' ? 'playing' : prev));

    };



    video.addEventListener('waiting', onWaiting);

    video.addEventListener('playing', onPlaying);

    return () => {

      video.removeEventListener('waiting', onWaiting);

      video.removeEventListener('playing', onPlaying);

      if (waitingTimer) clearTimeout(waitingTimer);

    };

  }, [hlsUrl]);



  useEffect(() => {

    onStreamStatusChangeRef.current?.(status);

  }, [status]);



  const formatPlaybackTime = (seconds: number): string => {

    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

    const h = Math.floor(seconds / 3600);

    const m = Math.floor((seconds % 3600) / 60);

    const s = Math.floor(seconds % 60);

    if (h > 0) {

      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

    }

    return `${m}:${String(s).padStart(2, '0')}`;

  };



  /** Hora real de la grabación que se está viendo (alineado con startOffsetSeconds del HLS) */

  const recordingCurrentTime =

    recordingStartTime && Number.isFinite(playbackTime)

      ? new Date(

        recordingStartTime.getTime() +

        Math.max(0, playbackTime - (startOffsetSeconds ?? 0)) * 1000

      )

      : null;



  const isRecording = Boolean(hlsUrl);



  return (

    <div className="video-stream">

      <video

        ref={videoRef}

        className={`stream-video ${status}${isRecording ? ' stream-video--contain' : ''}`}

        muted

        playsInline

        loop={false}

        autoPlay={!isRecording}

        preload={isRecording ? 'auto' : 'metadata'}

      />



      {status === 'placeholder' && (

        <div className="stream-overlay placeholder">

          <svg viewBox="0 0 24 24" fill="currentColor" className="placeholder-icon" style={{ width: 48, height: 48, opacity: 0.5 }}>

            <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />

          </svg>

          <span>{placeholderMessage}</span>

        </div>

      )}



      {status === 'loading' && (

        <div className="stream-overlay loading">

          <XTAMSpinner

            variant="small"

            message={isRecording ? 'Preparando grabación…' : `Conectando a ${cameraName}…`}

          />

        </div>

      )}



      {status === 'offline' && (

        <div className="stream-overlay offline">

          <svg viewBox="0 0 24 24" fill="currentColor" className="offline-icon">

            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zm-8-5.5l1.88 2.5H16l-3-4-2 2.67L9 12l-3 4h2.12L10 13.5l2 2.67z" />

          </svg>

          <span>{displayErrorMessage(errorMessage, 'Cámara offline')}</span>

        </div>

      )}



      {status === 'error' && (

        <div className="stream-overlay error">

          <svg viewBox="0 0 24 24" fill="currentColor" className="error-icon">

            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />

          </svg>

          <span>{displayErrorMessage(errorMessage, 'Error de conexión')}</span>

        </div>

      )}



      {status === 'playing' && !isRecording && (

        <div className="stream-info">

          <span className={`live-badge ${streamType === 'ws' ? 'ws' : ''}`}>

            ● {streamType === 'ws' ? 'WS' : 'LIVE'}

          </span>

        </div>

      )}

    </div>

  );

});



CameraVideoPlayer.displayName = 'CameraVideoPlayer';



export default CameraVideoPlayer;